// Вкладка «Запуск и исполнение» — Stage 2.
// Показывает готовность позиций к запуску (launch-readiness), позволяет
// пакетно запускать готовые позиции, управлять поручениями (WorkOrder)
// и контрольными событиями, запрашивать исключения по недоплате.
import { useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import { requests, sourcing, workflow } from '../../api/endpoints';
import type {
    ControlEvent, ExceptionApproval, LaunchReadiness, RequestItem,
    User, WorkOrder,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import {
    Card, Column, DataTable, Empty, ErrorBox, Field, fmtDate, fmtMoney,
    JsonBlock, shortId, StatusBadge,
} from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

const CHECK_LABELS: Record<string, string> = {
    agreement: 'Согласование',
    documents: 'Документы',
    product: 'Продукция',
    payer: 'Плательщик',
    legal_entity: 'Юрлицо',
    credit_policy: 'Кредитная политика',
    prepayment: 'Предоплата',
    currency: 'Валюта',
    already_launched: 'Уже запущена',
    readiness_rule: 'Правило готовности',
    item_status: 'Статус позиции',
};

function ItemReadiness({ item, selected, onToggle }: {
    item: RequestItem; selected: boolean; onToggle: (id: string) => void;
}) {
    const readiness = useAsync<LaunchReadiness>(
        () => requests.launchReadiness(item.id), [item.id, item.revision]);

    const r = readiness.data;
    const blocking = r?.blocking_reasons ?? [];
    const commercial = r?.commercial;

    return (
        <Card
            title={`${item.title} — ${item.document_type ?? ''}`}
            actions={<StatusBadge value={r ? (r.can_launch ? 'ready' : 'blocked') : 'loading'} />}
        >
            <ErrorBox error={readiness.error} onRetry={readiness.reload} />
            {r ? (
                <>
                    <div className="kv-grid" style={{ marginBottom: 8 }}>
                        <div className="kv"><span className="kv-key">Продажа</span><span className="kv-value">{fmtMoney(commercial?.agreed_sale, commercial?.currency)}</span></div>
                        <div className="kv"><span className="kv-key">Выставлено</span><span className="kv-value">{fmtMoney(commercial?.invoiced, commercial?.currency)}</span></div>
                        <div className="kv"><span className="kv-key">Оплачено нетто</span><span className="kv-value">{fmtMoney(commercial?.paid_net, commercial?.currency)}</span></div>
                        <div className="kv"><span className="kv-key">Требуется</span><span className="kv-value">{fmtMoney(commercial?.required, commercial?.currency)}</span></div>
                        <div className="kv"><span className="kv-key">Не хватает</span><span className="kv-value">{fmtMoney(commercial?.missing, commercial?.currency)}</span></div>
                        <div className="kv"><span className="kv-key">Кредитный риск</span><span className="kv-value">{fmtMoney(commercial?.credit_exposure, commercial?.currency)}</span></div>
                    </div>
                    {blocking.length > 0 ? (
                        <div className="error-box" role="alert">
                            Блокирующие причины: {blocking.map((b) => CHECK_LABELS[b] ?? b).join(', ')}
                        </div>
                    ) : null}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <input type="checkbox" checked={selected} disabled={!r.can_launch}
                               onChange={() => onToggle(item.id)} />
                        Включить в пакет запуска
                    </label>
                </>
            ) : null}
        </Card>
    );
}

function WorkOrderCard({ workOrder, events, assignees, onChanged }: {
    workOrder: WorkOrder; events: ControlEvent[]; assignees: Map<string, string>;
    onChanged: () => void;
}) {
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);
    const [promisedAt, setPromisedAt] = useState(workOrder.promised_due_at?.slice(0, 16) ?? '');
    const [promisedReason, setPromisedReason] = useState('');
    const [pauseReasons, setPauseReasons] = useState<Record<string, string>>({});

    const run = async (fn: () => Promise<unknown>) => {
        setBusy(true); setErr(null);
        try { await fn(); onChanged(); } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    const woEvents = events.filter((e) => e.work_order === workOrder.id);
    const activePause = (e: ControlEvent) => Boolean(e.has_active_pause);

    const eventCols: Column<ControlEvent>[] = [
        { key: 'kind', title: 'Тип', render: (r) => CHECK_LABELS[r.kind ?? ''] ?? r.kind ?? '—' },
        { key: 'status', title: 'Статус', render: (r) => <StatusBadge value={r.status} /> },
        { key: 'due_at', title: 'Срок', render: (r) => fmtDate(r.due_at) },
        { key: 'assignee', title: 'Исполнитель', render: (r) => (r.assignee != null ? (assignees.get(String(r.assignee)) ?? String(r.assignee)) : '—') },
        {
            key: 'actions', title: '', render: (r) => {
                const open = r.status === 'open' || r.status === 'in_progress' || r.status === 'overdue';
                if (!open && !activePause(r)) return null;
                return (
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        {open && !activePause(r) ? (
                            <>
                                <input className="input" style={{ width: 140 }} placeholder="Причина паузы"
                                       value={pauseReasons[r.id] ?? ''}
                                       onChange={(e) => setPauseReasons((p) => ({ ...p, [r.id]: e.target.value }))} />
                                <button className="btn small" disabled={busy || !(pauseReasons[r.id] ?? '').trim()}
                                        onClick={() => void run(() => workflow.pauseEvent(r.id, pauseReasons[r.id], r.revision))}>
                                    Пауза
                                </button>
                                <button className="btn small" disabled={busy}
                                        onClick={() => void run(() => workflow.completeEvent(r.id, r.revision))}>
                                    Завершить
                                </button>
                            </>
                        ) : null}
                        {activePause(r) ? (
                            <button className="btn small primary" disabled={busy}
                                    onClick={() => void run(() => workflow.resumeEvent(r.id, r.revision))}>
                                Возобновить
                            </button>
                        ) : null}
                    </span>
                );
            },
        },
    ];

    return (
        <Card
            title={`Поручение ${workOrder.type ?? ''} — ${shortId(workOrder.id)}`}
            actions={<StatusBadge value={workOrder.status} />}
        >
            <div className="kv-grid" style={{ marginBottom: 8 }}>
                <div className="kv"><span className="kv-key">Себестоимость</span><span className="kv-value">{fmtMoney(workOrder.cost, workOrder.currency)}</span></div>
                <div className="kv"><span className="kv-key">Исполнитель</span><span className="kv-value">{workOrder.assignee != null ? (assignees.get(String(workOrder.assignee)) ?? String(workOrder.assignee)) : '—'}</span></div>
                <div className="kv"><span className="kv-key">Внутренний срок</span><span className="kv-value">{fmtDate(workOrder.internal_due_at)}</span></div>
                <div className="kv"><span className="kv-key">Обещанный срок</span><span className="kv-value">{fmtDate(workOrder.promised_due_at)}</span></div>
                <div className="kv"><span className="kv-key">Операция</span><span className="kv-value">{workOrder.current_operation || '—'}</span></div>
                <div className="kv"><span className="kv-key">Принято</span><span className="kv-value">{fmtDate(workOrder.accepted_at)}</span></div>
                <div className="kv"><span className="kv-key">Цикл</span><span className="kv-value">{workOrder.cycle ?? '—'}</span></div>
            </div>

            <ErrorBox error={err} />
            <div className="form-actions" style={{ marginBottom: 12 }}>
                {workOrder.status === 'launched' && !workOrder.accepted_at ? (
                    <button className="btn small primary" disabled={busy}
                            onClick={() => void run(() => sourcing.acceptWorkOrder(workOrder.id, workOrder.revision))}>
                        Принять в работу
                    </button>
                ) : null}
            </div>

            <div className="section">
                <h3>События исполнения</h3>
                <DataTable columns={eventCols} rows={woEvents} empty="Событий нет" />
            </div>

            <div className="section" style={{ marginTop: 12 }}>
                <h3>Обещанный клиенту срок</h3>
                <div className="form-actions">
                    <Field label="Новый срок">
                        <input className="input" type="datetime-local" value={promisedAt}
                               onChange={(e) => setPromisedAt(e.target.value)} />
                    </Field>
                    <Field label="Причина изменения">
                        <input className="input" value={promisedReason}
                               onChange={(e) => setPromisedReason(e.target.value)} />
                    </Field>
                    <button className="btn small" disabled={busy || !promisedAt || !promisedReason.trim()}
                            onClick={() => void run(() => sourcing.setPromisedDue(
                                workOrder.id, new Date(promisedAt).toISOString(), promisedReason, workOrder.revision))}>
                        Изменить срок
                    </button>
                </div>
            </div>
        </Card>
    );
}

export function LaunchTab({ data }: { data: CardData }) {
    const { request, items } = data;
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [assigneeId, setAssigneeId] = useState<string>('');
    const [internalDue, setInternalDue] = useState('');
    const [promisedDue, setPromisedDue] = useState('');
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);
    const [launchResult, setLaunchResult] = useState<unknown>(null);

    const [excItemId, setExcItemId] = useState('');
    const [excAmount, setExcAmount] = useState('');
    const [excCurrency, setExcCurrency] = useState('RUB');
    const [excReason, setExcReason] = useState('');
    const [excErr, setExcErr] = useState<unknown>(null);

    const { user } = useAuth();
    const canDecideExceptions = Boolean(user?.is_superuser)
        || (user?.roles ?? []).some((r) => r === 'supervisor' || r === 'admin');

    const workOrders = useAsync<WorkOrder[]>(() => sourcing.workOrders(), [request.id]);
    const events = useAsync<ControlEvent[]>(() => workflow.controlEvents({ request: request.id }), [request.id]);
    const exceptions = useAsync<ExceptionApproval[]>(
        () => workflow.exceptionApprovals({ request: request.id }), [request.id]);
    const assignables = useAsync<User[]>(() => sourcing.assignableUsers(), []);

    const assignees = useMemo(() => new Map<string, string>(
        (assignables.data ?? []).map((u) => [String(u.id), u.full_name || u.username])),
        [assignables.data]);

    const requestWorkOrders = useMemo(() => {
        const itemIds = new Set(items.map((i) => i.id));
        return (workOrders.data ?? []).filter((w) => itemIds.has(w.item));
    }, [workOrders.data, items]);

    const launchedItemIds = new Set(requestWorkOrders.filter((w) => w.status === 'launched' || w.accepted_at).map((w) => w.item));
    const pendingItems = items.filter((i) => !launchedItemIds.has(i.id) && i.status !== 'cancelled');

    const toggle = (id: string) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const launch = async () => {
        setBusy(true); setErr(null); setLaunchResult(null);
        try {
            const result = await requests.launch(request.id, {
                revision: request.revision ?? 0,
                item_ids: [...selected],
                assignee_id: assigneeId || undefined,
                internal_due_at: internalDue ? new Date(internalDue).toISOString() : undefined,
                promised_due_at: promisedDue ? new Date(promisedDue).toISOString() : undefined,
            });
            setLaunchResult(result);
            setSelected(new Set());
            data.reload();
            workOrders.reload();
            events.reload();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    const requestExc = async () => {
        setBusy(true); setErr(null);
        try {
            await workflow.requestException({
                request_id: request.id,
                item_id: excItemId || null,
                kind: 'launch_underpaid',
                reason: excReason,
                amount: excAmount || null,
                currency: excCurrency,
            });
            setExcItemId(''); setExcAmount(''); setExcReason('');
            exceptions.reload();
        } catch (x) {
            setErr(x instanceof ApiError ? x : new Error(String(x)));
        } finally { setBusy(false); }
    };

    /** Решение руководителя по исключению (approve=true/false + revision). */
    const decideExc = async (approval: ExceptionApproval, approve: boolean) => {
        setBusy(true); setExcErr(null);
        try {
            const reason = approve
                ? (window.confirm('Одобрить исключение?') ? '' : null)
                : window.prompt('Причина отклонения:', '');
            if (reason === null) return;
            await workflow.decideException(approval.id, approve, reason, approval.revision);
            exceptions.reload();
        } catch (x) {
            setExcErr(x instanceof ApiError ? x : new Error(String(x)));
        } finally { setBusy(false); }
    };

    const excCols: Column<ExceptionApproval>[] = [
        { key: 'kind', title: 'Тип', render: (r) => r.kind ?? '—' },
        { key: 'item', title: 'Позиция', render: (r) => items.find((i) => i.id === r.item)?.title ?? (r.item ? shortId(r.item) : '—') },
        { key: 'amount', title: 'Сумма', render: (r) => fmtMoney(r.amount, r.currency) },
        { key: 'decision', title: 'Решение', render: (r) => <StatusBadge value={r.decision} /> },
        { key: 'consumed_at', title: 'Использовано', render: (r) => fmtDate(r.consumed_at) },
        { key: 'reason', title: 'Основание', render: (r) => r.reason ?? '—' },
        {
            key: 'actions', title: '', render: (r) => (
                r.decision === 'pending' && canDecideExceptions ? (
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                        <button className="btn small primary" disabled={busy}
                                onClick={() => void decideExc(r, true)}>
                            Одобрить
                        </button>
                        <button className="btn small danger" disabled={busy}
                                onClick={() => void decideExc(r, false)}>
                            Отклонить
                        </button>
                    </span>
                ) : null
            ),
        },
    ];

    return (
        <div>
            <Card title="Готовность к запуску">
                {pendingItems.length === 0 ? (
                    <Empty text="Все позиции запущены или отменены" />
                ) : (
                    pendingItems.map((item) => (
                        <ItemReadiness key={item.id} item={item}
                                       selected={selected.has(item.id)} onToggle={toggle} />
                    ))
                )}

                <ErrorBox error={err} />
                {launchResult ? <JsonBlock data={launchResult} /> : null}

                {pendingItems.length > 0 ? (
                    <div className="form-actions" style={{ marginTop: 12 }}>
                        <Field label="Исполнитель">
                            <select className="input" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                                <option value="">— не назначен —</option>
                                {(assignables.data ?? []).map((u) => (
                                    <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Внутренний срок">
                            <input className="input" type="datetime-local" value={internalDue}
                                   onChange={(e) => setInternalDue(e.target.value)} />
                        </Field>
                        <Field label="Обещанный клиенту срок">
                            <input className="input" type="datetime-local" value={promisedDue}
                                   onChange={(e) => setPromisedDue(e.target.value)} />
                        </Field>
                        <button className="btn primary" disabled={busy || selected.size === 0}
                                onClick={() => { if (window.confirm(`Запустить в работу ${selected.size} позиций?`)) void launch(); }}>
                            Запустить выбранные ({selected.size})
                        </button>
                    </div>
                ) : null}
            </Card>

            <Card title="Исключения по оплате">
                <DataTable columns={excCols} rows={exceptions.data ?? []} empty="Исключений нет" />
                <ErrorBox error={exceptions.error} />
                <ErrorBox error={excErr} />
                <div className="form-actions" style={{ marginTop: 8 }}>
                    <Field label="Позиция">
                        <select className="input" value={excItemId} onChange={(e) => setExcItemId(e.target.value)}>
                            <option value="">— выберите позицию —</option>
                            {items.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
                        </select>
                    </Field>
                    <Field label="Сумма дефицита">
                        <input className="input" value={excAmount} onChange={(e) => setExcAmount(e.target.value)} />
                    </Field>
                    <Field label="Валюта">
                        <select className="input" value={excCurrency} onChange={(e) => setExcCurrency(e.target.value)}>
                            {['RUB', 'USD', 'EUR', 'CNY'].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="Обоснование">
                        <input className="input" value={excReason} onChange={(e) => setExcReason(e.target.value)} />
                    </Field>
                    <button className="btn small" disabled={busy || !excItemId || !excReason.trim()} onClick={() => void requestExc()}>
                        Запросить исключение
                    </button>
                </div>
            </Card>

            <Card title="Поручения в работе" actions={<button className="btn" onClick={() => { workOrders.reload(); events.reload(); }}>Обновить</button>}>
                <ErrorBox error={workOrders.error} onRetry={workOrders.reload} />
                {requestWorkOrders.length === 0 ? (
                    <Empty text="Поручений нет" />
                ) : (
                    requestWorkOrders.map((wo) => (
                        <WorkOrderCard key={wo.id} workOrder={wo} events={events.data ?? []}
                                       assignees={assignees}
                                       onChanged={() => { workOrders.reload(); events.reload(); data.reload(); }} />
                    ))
                )}
            </Card>
        </div>
    );
}
